import React, { useState } from "react";
import withReduxProvider from "web/react/redux-provider";
import PropTypes from "prop-types";
import Button from "web/react/components/button/button";
import classnames from "classnames";
import analytics from "web/script/analytics/analytics";
import logging from "web/script/utils/logging";
import requester from "web/script/modules/requester";
import facebookEvents from "web/script/analytics/facebook-events";
import doubleclickEvents from "web/script/analytics/doubleclick-events";
import { incrementBasketCount } from "web/redux/ducks/app-header-cart";
import { openShippingAndReturnsOverlay } from "web/redux/ducks/shipping-and-returns-overlay";
import { connect } from "react-redux";
import { gettext } from "web/script/modules/django-i18n";
import { useGetSaveForLaterStatus } from "web/react/hooks/use-save-for-later/use-save-for-later";
import { pluck } from "underscore";
import SVGIcon from "../../svg-icon/svg-icon";
import styles from "./product-description-express-checkout.css";

function mapDispatchToProps(dispatch) {
    return {
        incrementBasketCount: () => dispatch(incrementBasketCount()),
        openShippingAndReturnsOverlay: (linkId) => dispatch(openShippingAndReturnsOverlay(linkId)),
    };
}

export function ProductDescriptionExpressCheckout({
    openShippingAndReturnsOverlay,
    className,
    incrementBasketCount,
    productId,
    sizeGuideLink,
    sizes,
    expressCheckoutRedesign,
}) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [buyButtonEnabled, setBuyButtonEnabled] = useState(true);
    const [isSavedForLater] = useGetSaveForLaterStatus(productId);
    const [errorMessage, setErrorMessage] = useState();

    function handleClickOverlay(event) {
        event.preventDefault();
        const optionValue = sizes[selectedIndex].option_value;
        openShippingAndReturnsOverlay(optionValue.link_id);
    }

    function handleSizeChange(event) {
        setSelectedIndex(event.target.selectedIndex);
        analytics.event("express_checkout", "changed_size");
    }

    function handleClickSizeGuide() {
        analytics.event("express_checkout", "show_size_guide");
    }

    function _sendBuyActionAnalytics() {
        const label = isSavedForLater ? "save_for_later_add_to_cart" : "add_to_cart_button";
        try {
            const analyticsPromises = [
                analytics.event("CART", "ADD_ITEM", label),
                analytics.tagEvent(
                    analytics.TAG_NETWORKS.FACEBOOK,
                    facebookEvents.ADD_TO_CART,
                    {
                        content_ids: productId,
                        content_type: "product",
                    },
                    true
                ),
                analytics.tagEvent(
                    analytics.TAG_NETWORKS.DOUBLECLICK,
                    doubleclickEvents.ADD_TO_CART,
                    {
                        path: window.location.pathname,
                        size: sizes[selectedIndex].raw_size,
                    }
                ),
            ];
            return Promise.all(analyticsPromises);
        } catch (e) {
            logging.error(e);
        }
    }

    async function addToBag() {
        setBuyButtonEnabled(false);
        const optionValue = sizes[selectedIndex].option_value;
        const requestObj = {
            icon: true,
            raw_size: optionValue.raw_size,
            link_id: optionValue.link_id,
        };

        const body = {
            buy_option: JSON.stringify(requestObj),
            is_base64_encoded: false,
        };

        _sendBuyActionAnalytics();

        let jsonData = undefined;
        try {
            jsonData = await requester.post("/cart/add/", body);
        } catch (e) {
            logging.error(e);
            analytics.event("CART", "ADD_TO_BAG_ERROR");
            setErrorMessage("Something went wrong!");
            return;
        }

        if (jsonData && jsonData.success) {
            incrementBasketCount();
        } else {
            let message = pluck(jsonData.data, "message").join(" ");
            setErrorMessage(message);

            if (
                message
                    .toLowerCase()
                    .indexOf("something went wrong trying to add this product to your cart") > -1
            ) {
                analytics.event("cart", "add_to_cart_error", "cart_add_item_error");
            } else if (
                message.toLowerCase().indexOf("this product seems to no longer be in stock") > -1
            ) {
                analytics.event("cart", "add_to_cart_error", "product_no_longer_in_stock");
            } else if (message.toLowerCase().indexOf("you need to select an option") > -1) {
                analytics.event("cart", "add_to_cart_error", "no_buy_option_detected");
            } else if (message.toLowerCase().indexOf("you need to select a size") > -1) {
                analytics.event("cart", "add_to_cart_error", "no_size_detected");
            } else if (message.toLowerCase().indexOf("you cannot have more than") > -1) {
                analytics.event("cart", "add_to_cart_error", "too_many_items_in_cart");
            }
        }

        setBuyButtonEnabled(true);
    }

    let addToBagButtonStyle = "temp-grey";
    if (expressCheckoutRedesign) {
        addToBagButtonStyle = "tertiary";
    }

    return (
        <div className={className}>
            <div
                className={classnames(styles.componentColumn, {
                    [styles.componentRow]: expressCheckoutRedesign,
                })}
            >
                <form className={styles.sizeForm}>
                    <div className={styles.selectDropDown}>
                        <select className={styles.selectDropDownInput} onChange={handleSizeChange}>
                            {sizes.map((size) => {
                                return (
                                    <option key={size.raw_size}>
                                        {size.human_size}
                                        {" - "}
                                        {size.min_price}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </form>

                {!expressCheckoutRedesign && (
                    <div className={styles.viewInfo}>
                        <a
                            href={sizeGuideLink}
                            onClick={handleClickSizeGuide}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {gettext("product.buy_area.view_size_guide")}
                        </a>
                    </div>
                )}

                <Button
                    onClick={addToBag}
                    fullWidth
                    styleType={addToBagButtonStyle}
                    disabled={!buyButtonEnabled}
                >
                    <div>
                        <SVGIcon name="basket-icon" />
                        <span>{gettext("product.buy_area.add_to_bag_btn_text")}</span>
                    </div>
                </Button>
                {!expressCheckoutRedesign && (
                    <div className={styles.viewInfo}>
                        <a href="#" onClick={handleClickOverlay}>
                            {gettext(
                                "product.buy_area.open_view_shipping_and_returns_info_overlay_btn_text"
                            )}
                        </a>
                    </div>
                )}
            </div>
            {expressCheckoutRedesign && (
                <div>
                    <div className={styles.viewInfo}>
                        <a
                            href={sizeGuideLink}
                            onClick={handleClickSizeGuide}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {gettext("product.buy_area.view_size_guide")}
                        </a>
                    </div>
                    <div className={styles.viewInfo}>
                        <a href="#" onClick={handleClickOverlay}>
                            {gettext(
                                "product.buy_area.open_view_shipping_and_returns_info_overlay_btn_text"
                            )}
                        </a>
                    </div>
                </div>
            )}
            {errorMessage && (
                <div className={styles.addToBagError}>
                    <em>{errorMessage}</em>
                </div>
            )}
        </div>
    );
}

ProductDescriptionExpressCheckout.propTypes = {
    sizeGuideLink: PropTypes.string,
    sizes: PropTypes.arrayOf(
        PropTypes.shape({
            option_value: PropTypes.shape({
                link_id: PropTypes.string,
                raw_size: PropTypes.string,
                icon: PropTypes.bool,
            }),
            human_size: PropTypes.string,
            raw_size: PropTypes.string,
            min_price: PropTypes.number,
        })
    ),
    className: PropTypes.string,
    incrementBasketCount: PropTypes.func,
    productId: PropTypes.string,
    expressCheckoutRedesign: PropTypes.bool,
    openShippingAndReturnsOverlay: PropTypes.func,
};

export default withReduxProvider(
    connect(
        null,
        mapDispatchToProps
    )(ProductDescriptionExpressCheckout)
);
